/**
 * AnalysisTrackerContext - Global Analysis Tracking (UX v2.0)
 *
 * OPARA v15.1: Progress Bar Fix - queued/pending status support
 * OPARA v14.2: Real-time Progress Sync - Backend'den gelen canlı veriler
 *
 * adil-kuyruk C2a (coklu-kutu KAPASITE - tracker GERCEKTEN 2 es-zamanli task kaldirir):
 * - Ic state array (activeAnalyses). startTracking APPEND + ENFORCE (dedupe + MAX_CONCURRENT_SINGLES=2)
 *   ve boolean doner (kabul/red). stopTracking(taskId?) -> taskId verilirse o task'i cikar, yoksa hepsini.
 * - Tuketiciler icin activeAnalysis = activeAnalyses[0] || null TURETILIR (backward-compat shim) ->
 *   GlobalAnalysisProgress / useAnalyzeReel / AnalysisForm C2a'da DOKUNULMAZ (map/guard/buton/i18n C2b).
 * - TEK setInterval; effect surucusu = non-terminal task_id KUMESI imzasi (activeSig), [0]-kilidi KALDIRILDI.
 *   Interval yasam-dongusu tum diziye gore; handler'lar interval'i OLDURMEZ (paylasilan interval korunur).
 *   7dk timeout PER-TASK (poll-ici yas-kontrolu; started_at persist -> refresh-dogru).
 * - TEK analiz davranisi C1/v30.95 ile BIREBIR (regresyon yok). 2-task UI'yi C2b tetikler.
 *
 * Features:
 * - LocalStorage sync for persistence (array + eski tek-obje geriye-uyum)
 * - Global polling at layout level (tek setInterval, poll dizinin TUM elemanini gezer)
 * - Real-time status messages from backend
 * - 7-minute per-task timeout (poll-ici yas-kontrolu)
 * - Error handling with retry
 * - v15.1: Support for queued/pending statuses (not just processing)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { getTranslation } from '../hooks/useTranslation';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// LocalStorage key TABANI. Gercek anahtar KULLANICI-BAZLI namespace'lenir (buildStorageKey).
const STORAGE_KEY = 'opara_active_analysis';

// TEK KAYNAK: kullanici-bazli storage anahtari. Uc ayri yerde string birlestirme YOK;
// tum oku/yaz/sil bu helper'in urettigi anahtari (storageKeyRef.current) kullanir.
// Amac: ayni tarayicida hesap degisince onceki hesabin analizi TASINMASIN (her hesap kendi
// namespace'ini okur; baska hesabin anahtarina hic bakmaz).
const buildStorageKey = (uid) => `${STORAGE_KEY}:${uid || 'anon'}`;

// Verilen anahtardan aktif (processing/queued/pending, <30dk, gecerli) analizleri dondurur (salt-oku).
const loadActiveFromStorage = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    // KRITIK geriye-uyum: eski tek-obje storage -> [obje]; yeni array -> oldugu gibi.
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    const now = new Date();
    const activeStatuses = ['processing', 'queued', 'pending'];
    return arr.filter((a) => {
      if (!a || !a.task_id || !a.username) return false;
      if (a.started_at) {
        const ageMinutes = (now - new Date(a.started_at)) / (1000 * 60);
        if (ageMinutes > 30) return false;
      }
      return activeStatuses.includes(a.status);
    });
  } catch (e) {
    console.error('[AnalysisTracker] Failed to parse stored analysis', e);
    return [];
  }
};

// Polling interval (5 seconds)
const POLL_INTERVAL = 5000;

// v14.2: 7 minute timeout for maintenance message
const ANALYSIS_TIMEOUT_MS = 7 * 60 * 1000;

// adil-kuyruk: es-zamanli tekil analiz tavani. C2a: startTracking'te ENFORCE edilir (early-guard C2b).
const MAX_CONCURRENT_SINGLES = 2;

const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'];

const AnalysisTrackerContext = createContext(null);

export const useAnalysisTracker = () => {
  const context = useContext(AnalysisTrackerContext);
  if (!context) {
    throw new Error('useAnalysisTracker must be used within AnalysisTrackerProvider');
  }
  return context;
};

export const AnalysisTrackerProvider = ({ children }) => {
  // AuthProvider bunu SARIYOR (App.js:341-342) -> useAuth guvenli. user.id namespace kaynagi.
  const { user } = useAuth();

  // Ic state: array (0..MAX_CONCURRENT_SINGLES eleman).
  const [activeAnalyses, setActiveAnalyses] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef(null);
  const mountedRef = useRef(true);

  // Aktif kullanici-bazli storage anahtari (TEK KAYNAK buildStorageKey). Callback'ler stale-closure
  // olmadan guncel anahtari gorsun diye ref. prevKeyRef: onceki anahtari (logout/hesap-degisimi temizligi).
  const storageKeyRef = useRef(buildStorageKey(user?.id));
  const prevKeyRef = useRef(storageKeyRef.current);

  // Backward-compat shim: eski tek-obje ismi yasasin (tuketiciler DOKUNULMAZ).
  const activeAnalysis = activeAnalyses[0] || null;

  // poll + startTracking/stopTracking'in GUNCEL diziyi senkron gormesi icin ref (stale-closure onleme).
  const activeAnalysesRef = useRef([]);
  useEffect(() => {
    activeAnalysesRef.current = activeAnalyses;
  }, [activeAnalyses]);

  // Mount/unmount: mountedRef + polling temizligi. (Cold-start restore user-effect'e tasindi.)
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  // KULLANICI-BAZLI namespace: user.id degisince (mount, login, logout, hesap-degisimi) calisir.
  //   - Yeni anahtari uret (buildStorageKey TEK KAYNAK) ve storageKeyRef'e yaz.
  //   - Anahtar degistiyse: ONCEKI hesabin anahtarini SIL (logout & hesap-degisimi temizligi = req7)
  //     ve eski hesabin polling'ini durdur -> onceki analizi TASIMA (req8: yeni hesap yalniz kendi
  //     namespace'ini okur, eski anahtara hic bakmaz).
  //   - Ardindan YENI anahtardan (bos olabilir) cold-start restore. Boylece hesap A'nin izlemesi
  //     hesap B'ye asla sizmaz; ayni tarayicida guvenli izolasyon.
  useEffect(() => {
    const newKey = buildStorageKey(user?.id);
    const oldKey = prevKeyRef.current;
    storageKeyRef.current = newKey;

    if (oldKey !== newKey) {
      try { localStorage.removeItem(oldKey); } catch (_) {}
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        setIsPolling(false);
      }
    }
    prevKeyRef.current = newKey;

    const restored = loadActiveFromStorage(newKey);
    if (restored.length > 0) {
      console.log('[AnalysisTracker] Cold start: Restoring', restored.length, 'active analysis for', newKey);
      // normalize: temizlenmis diziyi array-formatinda geri yaz (eski tek-obje -> array migrasyonu)
      localStorage.setItem(newKey, JSON.stringify(restored));
    }
    setActiveAnalyses(restored);
  }, [user?.id]);

  // Start tracking - C2a: APPEND + ENFORCE. boolean doner (kabul=true / red=false).
  // - Ayni task_id zaten varsa NO-OP -> false (dedupe).
  // - Dizi >= MAX_CONCURRENT_SINGLES ise EKLEME -> false (tavan).
  // - Aksi halde [...prev, yeni] -> true.
  // (Karar activeAnalysesRef.current uzerinden SENKRON; boolean donus dogru. Early-guard/i18n C2b.)
  const startTracking = useCallback((taskId, username, locale = 'tr') => {
    const prev = activeAnalysesRef.current;

    // dedupe: ayni task zaten izleniyor
    if (prev.some((a) => a.task_id === taskId)) {
      console.log('[AnalysisTracker] startTracking NO-OP (already tracked)', taskId);
      return false;
    }
    // tavan: MAX_CONCURRENT_SINGLES dolu
    if (prev.length >= MAX_CONCURRENT_SINGLES) {
      console.log('[AnalysisTracker] startTracking REJECTED (capacity full)', prev.length);
      return false;
    }

    const analysisData = {
      task_id: taskId,
      username: username,
      status: 'processing',
      progress: 0,
      message: getTranslation('progress.starting', {}, locale),
      locale: locale,
      started_at: new Date().toISOString()
    };

    console.log('[AnalysisTracker] Starting tracking (append)', analysisData);

    const next = [...prev, analysisData];
    activeAnalysesRef.current = next; // senkron: sonraki cagri taze diziyi gorur (back-to-back atomik)
    setActiveAnalyses(next);
    localStorage.setItem(storageKeyRef.current, JSON.stringify(next));
    return true;
  }, []);

  // Stop tracking - C2a: taskId opsiyonel.
  // - taskId verilirse: SADECE o task'i cikar (filter). Dizi bosalirsa interval'i temizle.
  // - taskId yoksa (backward-compat: mevcut cagirilar): hepsini temizle + interval.
  const stopTracking = useCallback((taskId) => {
    if (taskId) {
      const next = activeAnalysesRef.current.filter((a) => a.task_id !== taskId);
      console.log('[AnalysisTracker] Stopping tracking (task)', taskId, '-> kalan', next.length);
      activeAnalysesRef.current = next; // senkron: sonraki cagri taze diziyi gorur
      setActiveAnalyses(next);
      if (next.length > 0) {
        localStorage.setItem(storageKeyRef.current, JSON.stringify(next));
      } else {
        localStorage.removeItem(storageKeyRef.current);
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsPolling(false);
      }
      return;
    }

    // backward-compat: hepsini temizle
    console.log('[AnalysisTracker] Stopping tracking (all)');
    activeAnalysesRef.current = []; // senkron: sonraki cagri taze diziyi gorur
    setActiveAnalyses([]);
    localStorage.removeItem(storageKeyRef.current);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Check task status from API (task_id-param -> her task icin bagimsiz cagrilabilir)
  const checkTaskStatus = useCallback(async (taskId) => {
    // Progress endpoint AUTH ZORUNLU + sahiplik kontrollu (IDOR fix, COMMIT-1 4a3d73aa).
    // ACIK Authorization header (uygulamadaki diger axios cagrilarinin token kaynagi =
    // localStorage 'auth_token'; AuthContext token'i ile kilit-adimda, cookie-only oturumda
    // ikisi de bos olur ve cookie yedegine dusulur). credentials:'include' cookie yolunu yedek tutar.
    let response;
    try {
      const authToken = localStorage.getItem('auth_token');
      response = await fetch(`${API_BASE}/api/task_progress/${taskId}`, {
        credentials: 'include',
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
    } catch (netErr) {
      // GERCEK network hatasi (fetch reject) -> GECICI. null=skip -> task non-terminal kalir,
      // poll bir sonraki tick'te tekrar dener (eski davranis korunur).
      console.error('[AnalysisTracker] Network error checking status', netErr);
      return null;
    }

    // "Task artik ERISILEMEZ" ile "gecici hata"yi AYIR (KUSUR-2 fix):
    //   403 (task baskasinin) / 404 (task silinmis/purge) -> task bir daha ERISILEMEZ. Bunu
    //   TERMINAL say (sentetik failed, progress 0) -> poll handleFailed'i cagirir, task aktif
    //   listede terminal olur, activeSig'ten duser, interval durur. Yoksa auth-guard sonrasi bu
    //   yanitlar throw->null olup poll fail'i HIC gormeden %99'da SONSUZ donuyordu.
    if (response.status === 403 || response.status === 404) {
      console.warn(`[AnalysisTracker] Task ${taskId} inaccessible (HTTP ${response.status}) -> terminal`);
      return {
        task_id: taskId,
        status: 'failed',
        progress_percent: 0,
        progress_percentage: 0,
        error_message: 'unavailable',
      };
    }

    // 401 (token expired) veya diger non-2xx -> GECICI/auth. Mevcut davranis: skip+retry (null).
    // (Oturum bozuksa kullanici zaten logout olur; kalan takilma 7-dk per-task timeout'ta terminal olur.)
    if (!response.ok) {
      console.error('[AnalysisTracker] Non-OK status (transient)', response.status);
      return null;
    }

    try {
      return await response.json();
    } catch (parseErr) {
      console.error('[AnalysisTracker] Failed to parse status', parseErr);
      return null;
    }
  }, []);

  // C2a: Effect surucusu = non-terminal task_id KUMESI imzasi (SADECE kume degisince degisir;
  // progress-tick'lerinde DEGIL -> interval thrashing yok). [0]-kilidi KALKTI.
  const activeSig = activeAnalyses
    .filter((a) => !TERMINAL_STATUSES.includes(a.status))
    .map((a) => a.task_id)
    .sort()
    .join('|');

  // Polling effect - v14.1 FIX: Race condition fixed
  // C2a: TEK setInterval; poll dizinin TUM non-terminal elemanini gezer (ref'ten guncel).
  // Interval yasam-dongusu activeSig'e gore; handler'lar interval'i OLDURMEZ (paylasilan).
  useEffect(() => {
    // Bir task'in progress guncellemesi (array.map ile ilgili task_id girdisini gunceller)
    const applyStatus = (tid, status) => {
      // v14.2: Use progress_percentage (not progress_percent) - Handbook v11.9 uyumu
      const progressValue = status.progress_percentage ?? status.progress_percent ?? 0;
      const statusMessage = status.status_message || status.progress_message || '';
      setActiveAnalyses((prev) => {
        const updated = prev.map((a) =>
          a.task_id === tid
            ? { ...a, status: status.status, progress: progressValue, message: statusMessage }
            : a
        );
        localStorage.setItem(storageKeyRef.current, JSON.stringify(updated));
        return updated;
      });
    };

    // Tamamlanma - ilgili task icin (a: dizi elemani -> per-task a.*).
    // C2a: interval'i OLDURMEZ (diger task'in polling'i kesilmesin); interval kararini effect verir.
    const handleCompleted = (a, status) => {
      const tid = a.task_id;
      console.log('[AnalysisTracker] Analysis completed!', tid);

      // v14.3: Store the full result for HomePage to consume
      const fullResult = status.result;
      const currentUsername = a.username;
      const currentLocale = a.locale || 'tr';

      setActiveAnalyses((prev) => {
        const updated = prev.map((x) =>
          x.task_id === tid
            ? {
                ...x,
                status: 'completed',
                progress: 100,
                message: getTranslation('progress.analysis_complete', {}, currentLocale),
                result: fullResult // v14.3: Store result for HomePage
              }
            : x
        );
        localStorage.setItem(storageKeyRef.current, JSON.stringify(updated));
        return updated;
      });

      const reportUrl = `/${currentLocale}/rapor/${tid}`;
      toast.success(
        <div className="flex flex-col gap-2">
          <span className="font-bold">{getTranslation('progress.analysis_complete', {}, currentLocale)}</span>
          <span className="text-sm">{getTranslation('notifications.analysis_ready', { username: currentUsername }, currentLocale)}</span>
          <a
            href={reportUrl}
            className="mt-1 px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-lg text-center hover:bg-green-600 transition-colors"
          >
            {getTranslation('progress.view_report', {}, currentLocale)}
          </a>
        </div>,
        {
          duration: 10000,
          id: `analysis-complete-${tid}` // per-task -> 2 task ayni anda bitince cakismaz
        }
      );

      // v14.3: Don't auto-stop tracking - let HomePage consume the result first
      // HomePage will call stopTracking after setting its result state
    };

    // Basarisizlik - ilgili task icin. C2a: interval'i OLDURMEZ.
    const handleFailed = (a, status) => {
      const tid = a.task_id;
      console.log('[AnalysisTracker] Analysis failed!', tid);

      const currentLocale = a.locale || 'tr';

      // COMMIT-C2b: YENI MODEL - basarisiz analiz yok. TUM terminal-fail'ler BIRLESIK mesaj (iade ibaresi YOK).
      //   wall_category/error_type backend'de persist KALIR (C3 staff triyaj); sadece MUSTERI mesaji birlesir.
      //   NOT: bu handler yalniz TERMINAL-FAIL'de calisir; "Cok Dusuk/Hesaplanamadi" (tamamlanmis verdict) buradan GECMEZ.
      // FAZ-B: PROFILE_NOT_FOUND ISTISNA -> hesap YOK, "e-posta gonderecegiz" YANLIS vaat; net "bulunamadi" metni.
      //   Diger tum kategoriler rescue_pending (havuz/retry) AYNEN. TaskStatusResponse wall_category/error_type tasir.
      const _isPnf = (status.wall_category === 'PROFILE_NOT_FOUND') || (status.error_type === 'PROFILE_NOT_FOUND');
      const failMsg = getTranslation(_isPnf ? 'errors.profile_not_found' : 'errors.analysis_rescue_pending', {}, currentLocale);

      toast.error(
        <div className="flex flex-col gap-2">
          <span className="font-bold">{getTranslation('progress.analysis_failed', {}, currentLocale)}</span>
          <span className="text-sm">{failMsg}</span>
        </div>,
        {
          duration: Infinity,
          id: `analysis-failed-${tid}` // per-task -> 2 task ayni anda basarisiz olunca cakismaz
        }
      );

      setActiveAnalyses((prev) => {
        const updated = prev.map((x) =>
          x.task_id === tid ? { ...x, status: 'failed', error: status.user_message || status.error_message } : x
        );
        localStorage.setItem(storageKeyRef.current, JSON.stringify(updated));
        return updated;
      });
    };

    // C2a: PER-TASK 7dk timeout (poll-ici yas-kontrolu; started_at persist -> refresh-dogru).
    const handleTimeout = (a) => {
      const tid = a.task_id;
      const currentLocale = a.locale || 'tr';
      console.log('[AnalysisTracker] 7 minute timeout reached for', tid);

      setActiveAnalyses((prev) => {
        const updated = prev.map((x) =>
          x.task_id === tid ? { ...x, status: 'failed', error: 'timeout_maintenance' } : x
        );
        localStorage.setItem(storageKeyRef.current, JSON.stringify(updated));
        return updated;
      });

      toast.error(
        getTranslation('progress.timeout_maintenance', {}, currentLocale),
        {
          duration: 10000,
          id: `analysis-timeout-${tid}` // per-task -> coklu timeout cakismaz
        }
      );
    };

    const poll = async () => {
      if (!mountedRef.current) return;

      // Dizinin TUM non-terminal elemanini gez - her task icin bagimsiz.
      const tasks = activeAnalysesRef.current;
      const now = Date.now();
      for (const a of tasks) {
        if (TERMINAL_STATUSES.includes(a.status)) continue; // terminal olani atla

        // (d) per-task 7dk timeout: yas asildiysa isaretle, bu cycle poll etme.
        if (a.started_at && now - new Date(a.started_at).getTime() > ANALYSIS_TIMEOUT_MS) {
          handleTimeout(a);
          continue;
        }

        const status = await checkTaskStatus(a.task_id);
        if (!mountedRef.current) return;
        if (!status) continue;

        applyStatus(a.task_id, status);

        if (status.status === 'completed') handleCompleted(a, status);
        if (status.status === 'failed') handleFailed(a, status);
      }
    };

    // (b) INTERVAL YASAM-DONGUSU (tum diziye gore):
    if (activeSig === '') {
      // hic non-terminal yok -> durdur
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      setIsPolling(false);
      return;
    }

    // non-terminal var: interval YOKSA baslat (VARSA dokunma -> poll ref'ten yeni task'leri gezer)
    if (!pollingRef.current) {
      console.log('[AnalysisTracker] Starting polling loop for', activeSig);
      setIsPolling(true);
      poll(); // Initial poll
      pollingRef.current = setInterval(poll, POLL_INTERVAL);
    }

    // NOT: interval'i re-render'da OLDURMUYORUZ; handler'lar da oldurmuyor (paylasilan interval).
    // Interval yalniz activeSig='' (tum terminal) VEYA unmount'ta (mount-effect cleanup) temizlenir.
  }, [activeSig, checkTaskStatus]);

  // Retry failed analysis - C2b: per-task. Ilgili task'i ref'ten bul, o task'in locale'iyle yonlendir.
  const retryAnalysis = useCallback((taskId) => {
    // per-task: hedef task'i ref'ten bul
    const target = activeAnalysesRef.current.find((a) => a.task_id === taskId);
    if (target && target.status === 'failed') {
      stopTracking(taskId);
      window.location.href = `/${target.locale || 'tr'}/`;
      return;
    }
    // fallback (task bulunamadi): mevcut davranis - [0] shim ile
    if (activeAnalysis && activeAnalysis.status === 'failed') {
      stopTracking();
      window.location.href = `/${activeAnalysis.locale || 'tr'}/`;
    }
  }, [activeAnalysis, stopTracking]);

  // Dismiss/clear active analysis - C2b: per-task (verilen task'i kapatir).
  const dismissAnalysis = useCallback((taskId) => {
    stopTracking(taskId);
  }, [stopTracking]);

  const value = {
    activeAnalysis,                       // backward-compat (derived = activeAnalyses[0] || null)
    activeAnalyses,                       // C2a: array (C2b coklu-kutu tuketir)
    activeCount: activeAnalyses.length,   // C2a: opsiyonel (C2b icin)
    MAX_CONCURRENT_SINGLES,               // C2a: tavan (startTracking enforce eder; early-guard C2b)
    isPolling,
    startTracking,                        // C2a: boolean doner (kabul/red)
    stopTracking,                         // C2a: (taskId?) opsiyonel
    retryAnalysis,
    dismissAnalysis
  };

  return (
    <AnalysisTrackerContext.Provider value={value}>
      {children}
    </AnalysisTrackerContext.Provider>
  );
};

export default AnalysisTrackerContext;
